/**
 * Persistent "your scan is still running" strip.
 *
 * Purpose: the active job must stay visible and reachable from every Auto
 * Sourcing tab. Before this, navigating to Search hid the run entirely — the
 * header still said "Running" but there was no way back to the job, and the
 * Results tab could re-derive a different scan.
 *
 * It never invents state: the label comes from the server's `phase` and the
 * wait time from `queue_wait_seconds` (frozen once scanning starts).
 */
import React from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDuration, phasePresentation, queueWaitSeconds } from "./runSummaryFormat";

/** "dermstore_com" → "Dermstore" without inventing a retailer name. */
export function retailerLabel(run, sources = []) {
  const ids = Array.isArray(run?.source_ids) ? run.source_ids.filter(Boolean) : [];
  const names = ids.map((id) => {
    const found = sources.find((s) => s.source_id === id);
    const raw = found?.name || id;
    return String(raw)
      .replace(/_(com|co|net|org)$/i, "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  });
  if (!names.length) return "Scan";
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

export default function ActiveScanBanner({
  run,
  sources = [],
  onViewResults,
  now = Date.now(),
  className,
}) {
  if (!run?.run_id) return null;
  const phase = phasePresentation(run);
  const queued = String(run?.status || "").toLowerCase() === "queued";
  const waited = queueWaitSeconds(run, now);
  const position = Number(run?.queue?.position) || null;
  const collected = Number(run?.progress?.products_collected) || 0;

  const detail = [];
  if (queued) {
    detail.push("waiting for an available worker");
    if (waited != null) detail.push(`waiting ${formatDuration(waited)}`);
    if (position && position > 1) detail.push(`position ${position}`);
  } else if (collected > 0) {
    detail.push(`${collected} products collected`);
  } else {
    detail.push("starting up");
  }

  return (
    <div
      className={cn("as-active-scan", className)}
      data-testid="as-active-scan"
      data-run-id={run.run_id}
    >
      <span className="as-active-scan-spin" aria-hidden="true">
        <Loader2 className="as-active-scan-svg" />
      </span>
      <span className="as-active-scan-text">
        <strong data-testid="as-active-scan-retailer">{retailerLabel(run, sources)}</strong>
        <span className="as-active-scan-status" data-testid="as-active-scan-status">
          {phase.label}
        </span>
        <span className="as-active-scan-detail" data-testid="as-active-scan-queue">
          {detail.join(" · ")}
        </span>
      </span>
      {typeof onViewResults === "function" ? (
        <button
          type="button"
          className="as-active-scan-view"
          onClick={() => onViewResults(run)}
          data-testid="as-active-scan-view"
          data-run-id={run.run_id}
        >
          View Results
          <ArrowRight className="as-active-scan-svg" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
